/**
 * Comparison periods for the Multi-Clinic P&L: two weeks, months, quarters, or
 * any two date ranges, side by side.
 *
 * A period is { from: Date, to: Date }, both local calendar days, inclusive.
 * Defaults are the last COMPLETE period and the one before it: a partial
 * current month against a full prior one reads as a collapse that isn't.
 */

import {
  format, addDays, subDays, startOfWeek, startOfMonth, endOfMonth, subMonths,
  startOfQuarter, endOfQuarter, subQuarters, getQuarter, isAfter, isSameYear,
} from 'date-fns'

export const PERIOD_TYPES = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'custom', label: 'Custom' },
]

/** Monday–Sunday week starting at `start`. */
export function weekPeriod(start) {
  return { from: start, to: addDays(start, 6) }
}

export function monthPeriod(date) {
  return { from: startOfMonth(date), to: endOfMonth(date) }
}

export function quarterPeriod(date) {
  return { from: startOfQuarter(date), to: endOfQuarter(date) }
}

/** [A, B]: the last complete period of this type and the one before it. */
export function defaultPeriods(type, today = new Date()) {
  switch (type) {
    case 'month': {
      const a = subMonths(startOfMonth(today), 1)
      return [monthPeriod(a), monthPeriod(subMonths(a, 1))]
    }
    case 'quarter': {
      const a = subQuarters(startOfQuarter(today), 1)
      return [quarterPeriod(a), quarterPeriod(subQuarters(a, 1))]
    }
    case 'custom': {
      // Last 30 days through yesterday, against the 30 before.
      const to = subDays(today, 1)
      const from = subDays(to, 29)
      return [{ from, to }, { from: subDays(from, 30), to: subDays(from, 1) }]
    }
    default: {
      const thisMonday = startOfWeek(today, { weekStartsOn: 1 })
      return [weekPeriod(subDays(thisMonday, 7)), weekPeriod(subDays(thisMonday, 14))]
    }
  }
}

/**
 * Pickable months or quarters, newest first. The current one is included and
 * marked "to date", since it is still filling in.
 */
export function periodOptions(type, today = new Date(), count = type === 'quarter' ? 8 : 24) {
  const options = []
  for (let i = 0; i < count; i++) {
    const period = type === 'quarter'
      ? quarterPeriod(subQuarters(today, i))
      : monthPeriod(subMonths(today, i))
    const label = periodLabel(type, period)
    options.push({
      value: format(period.from, 'yyyy-MM-dd'),
      label: i === 0 ? `${label} (to date)` : label,
      period,
    })
  }
  return options
}

/** "Oct 6 – Oct 12", "Sep 2026", "Q3 2026", or "Sep 1 – Oct 15, 2026". */
export function periodLabel(type, { from, to } = {}) {
  if (!from || !to) return ''
  if (type === 'month') return format(from, 'MMM yyyy')
  if (type === 'quarter') return `Q${getQuarter(from)} ${format(from, 'yyyy')}`
  if (type === 'week') return `${format(from, 'MMM d')} – ${format(to, 'MMM d')}`
  if (isSameYear(from, to)) return `${format(from, 'MMM d')} – ${format(to, 'MMM d, yyyy')}`
  return `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`
}

/** Column heading for a period: "Week A", "Month B", "Period A". */
export function periodNoun(type) {
  return type === 'custom' ? 'Period' : PERIOD_TYPES.find(t => t.key === type)?.label || 'Week'
}

/** Calendar days in a period, inclusive. */
export function periodDays({ from, to }) {
  return Math.round((startOfDay(to) - startOfDay(from)) / 86400000) + 1
}

const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate())

/** True when the period has not finished yet (it ends today or later). */
export function isPartial({ to }, today = new Date()) {
  return !isAfter(startOfDay(today), startOfDay(to))
}

/** Query params for the API: start and inclusive end of each period. */
export function periodParams(a, b) {
  return {
    weekAStart: format(a.from, 'yyyy-MM-dd'),
    weekAEnd: format(a.to, 'yyyy-MM-dd'),
    weekBStart: format(b.from, 'yyyy-MM-dd'),
    weekBEnd: format(b.to, 'yyyy-MM-dd'),
  }
}
